import React, { useState } from 'react';
import { GetPublicContentGuidelineById_guideline } from '@frontend/app/queries/types/GetPublicContentGuidelineById';
import { GetPublicTermsSettingsForProgramQuery_termsTemplate } from '@frontend/app/queries/types/GetPublicTermsSettingsForProgramQuery';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { CatalogOrderRequestInput } from '@frontend/app/types/globalTypes';
import { usePreviewNextRoute, useRecruitment } from '../RecruitmentContext';
import { Hero } from './Amplify/Hero';
import { About } from './Amplify/About';
import { Perks } from './Amplify/Perks';
import { Persona } from './Amplify/Persona';
import { ViewMode } from '../constants';
import { Forms } from './Amplify/Forms';
import { BriefPagePreview } from './BriefPagePreview';
import { ProductPagePreview } from './ProductPagePreview';
import { Hero as HeroPulse } from './Pulse/Hero';
import { Form } from './Form';
import { Confirmation } from './Confirmation';

interface PreviewBodyProps {
  programTitle: string;
  viewMode: ViewMode;
  termsSettings: GetPublicTermsSettingsForProgramQuery_termsTemplate;
  contentGuideline: GetPublicContentGuidelineById_guideline;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (values: Record<string, any>, catalogOrderRequest?: CatalogOrderRequestInput) => void;
  isSubmitting: boolean;
}

export const PreviewBody = ({
  programTitle,
  viewMode,
  termsSettings,
  contentGuideline,
  onSubmit,
  isSubmitting,
}: PreviewBodyProps) => {
  const { amplifyTemplate, pulseTemplate, settings, setCatalogOrderRequest, catalogOrderRequest } = useRecruitment();

  const homePageAmplify = amplifyTemplate?.pages?.[0];
  const homePagePulse = pulseTemplate?.pages?.[0];
  const briefPage = amplifyTemplate?.pages?.find((page) => !!page.brief) || pulseTemplate?.pages?.find((page) => !!page.brief);

  const productPage = amplifyTemplate?.pages?.find((page) => !!page.product) || pulseTemplate?.pages?.find((page) => !!page.product);

  const formPage = amplifyTemplate?.pages?.find((page) => !!page.form) || pulseTemplate?.pages?.find((page) => !!page.form);

  const confirmationPage = amplifyTemplate?.pages?.find((page) => !!page.confirmation) || pulseTemplate?.pages?.find((page) => !!page.confirmation);

  const isHeroAvailable = homePageAmplify?.home?.hero?.showSection;
  const isAboutAvailable = homePageAmplify?.home?.about?.showSection;
  const isPerksAvailable = homePageAmplify?.home?.perks?.showSection;
  const isPersonaAvailable = homePageAmplify?.home?.persona?.showSection;
  const isFormAvailable = homePageAmplify?.home?.form?.showSection;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const { path, url } = useRouteMatch();

  const history = useHistory();

  const { search } = useLocation();

  const nextPageRoute = usePreviewNextRoute();

  return (
    <div className="w-full h-full @container">
      <Switch>
        <Route path={path} exact>
          {homePageAmplify && (
          <>
            {isHeroAvailable && (
              <Hero
                view={viewMode}
                logo={settings.logo}
                title={homePageAmplify.home.hero.title || 'Hero Title'}
                description={homePageAmplify.home.hero.description || 'Hero Description'}
                joinButtonText={homePageAmplify.home.hero.primaryButtonLabel || 'Add Label'}
                campaignStartDate={settings.campaignDates.startDate}
                campaignEndDate={settings.campaignDates.endDate}
                images={
                  homePageAmplify.home.hero.carouselImages?.map((image) => ({
                    url: image,
                    alt: 'Hero Image',
                  })) || []
                }
                backgroundColor={settings.bgPrimary}
                bodyTextColor={settings.bodyPrimary}
                bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                bodyTextFontFamily={settings.bodyFont.fontFamily}
                titleColor={settings.titlePrimary}
                titleFontSize={Number(settings.titleFont.fontSize)}
                titleFontFamily={settings.titleFont.fontFamily}
                joinButtonColor={settings.buttonBg}
                joinButtonTextColor={settings.buttonTextColor}
                joinButtonFontSize={settings.buttonFont.fontSize}
                joinButtonFontFamily={settings.buttonFont.fontFamily}
              />
            )}
            {isAboutAvailable && (
              <About
                view={viewMode}
                title={homePageAmplify.home.about.title || 'About Title'}
                description={homePageAmplify.home.about.description || 'About Description'}
                backgroundColor={settings.bgSecondary}
                titleColor={settings.titleSecondary}
                titleFontSize={Number(settings.titleFont.fontSize)}
                titleFontFamily={settings.titleFont.fontFamily}
                bodyTextColor={settings.bodySecondary}
                bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                bodyTextFontFamily={settings.bodyFont.fontFamily}
                images={
                  homePageAmplify.home.about.carouselImages?.map((image) => ({
                    url: image,
                    alt: 'About Image',
                  })) || []
                }
              />
            )}
            {isPerksAvailable && (
              <Perks
                view={viewMode}
                title={homePageAmplify.home.perks.title || 'Program Perks'}
                perks={
                  homePageAmplify.home.perks.listItems?.map((item) => ({
                    text: item.text,
                    icon: item.icon,
                  })) || []
                }
                listStyleType={homePageAmplify.home.perks.listStyleType || 'number'}
                backgroundColor={settings.bgPrimary}
                titleColor={settings.titlePrimary}
                titleFontSize={Number(settings.titleFont.fontSize)}
                titleFontFamily={settings.titleFont.fontFamily}
                bodyTextColor={settings.bodyPrimary}
                bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                bodyTextFontFamily={settings.bodyFont.fontFamily}
              />
            )}
            {isPersonaAvailable && (
              <Persona
                view={viewMode}
                image={homePageAmplify.home.persona.personaImage || ''}
                listStyleType={homePageAmplify.home.persona.listStyleType || 'bullet'}
                title={homePageAmplify.home.persona.title || 'Persona Title'}
                items={homePageAmplify.home.persona.listItems?.map((item) => ({
                  text: item.text,
                  icon: item.icon,
                })) || []}
                backgroundColor={settings.bgSecondary}
                titleColor={settings.titleSecondary}
                titleFontSize={Number(settings.titleFont.fontSize)}
                titleFontFamily={settings.titleFont.fontFamily}
                bodyTextColor={settings.bodySecondary}
                bodyTextFontSize={Number(settings.bodyFont.fontSize)}
                bodyTextFontFamily={settings.bodyFont.fontFamily}
              />
            )}
            {isFormAvailable && (
              <Forms
                view={viewMode}
                title={homePageAmplify.home.form.title || 'Form Title'}
                description={homePageAmplify.home.form.description || 'Form Description'}
                backgroundColor={settings.bgPrimary}
                cardColor={settings.bgSecondary}
                buttonColor={settings.buttonBg}
                buttonTextColor={settings.buttonTextColor}
                buttonLabel="Add Label"
                buttonFontSize={settings.buttonFont.fontSize}
                buttonFontFamily={settings.buttonFont.fontFamily}
                fields={homePageAmplify.home.form.fields || []}
                onSubmit={(values) => {
                  if (nextPageRoute === '/confirmation') {
                    onSubmit?.(values);
                  }
                  setFormValues(values);
                  history.push(`${url}${nextPageRoute}${search}`);
                }}
                socialMedia={Object.entries(settings?.socialAccounts || {}).map(([platform, url]) => ({
                  platform,
                  url,
                }))}
                isPublished={settings.isPublished}
                footerLinks={[
                  { label: 'Terms of Service', href: settings?.termsOfServiceUrl || 'https://www.aspire.io/legal/terms-of-service' },
                  { label: 'Privacy Policy', href: settings?.privacyPolicyUrl || 'https://www.aspire.io/legal/privacy-policy' },
                ]}
              />
            )}
          </>
        )}
          {homePagePulse && (
          <HeroPulse
            view={viewMode}
            logo={settings.logo}
            title={pulseTemplate.pages[0].hero.title}
            description={pulseTemplate.pages[0].hero.description}
            joinButtonText={pulseTemplate.pages[0].hero.ctaButtonText || 'Add Label'}
            campaignStartDate={settings.campaignDates.startDate}
            campaignEndDate={settings.campaignDates.endDate}
            images={
              pulseTemplate.pages[0].hero.carouselImages?.map((image) => ({
                url: image,
                alt: 'Hero Image',
              })) || []
            }
            socialMedia={Object.entries(settings?.socialAccounts || {}).map(([platform, url]) => ({
              platform,
              url,
            }))}
            listItems={pulseTemplate.pages[0].hero.listItems?.map((item) => ({
              text: item.text,
              icon: item.icon,
            }))}
            listStyleType={pulseTemplate.pages[0].hero.listStyleType}
            backgroundColor={settings.bgPrimary}
            bodyTextColor={settings.bodyPrimary}
            backgroundColorSecondary={settings.bgSecondary}
            bodyTextColorSecondary={settings.bodySecondary}
            bodyTextFontSize={Number(settings.bodyFont.fontSize)}
            bodyTextFontFamily={settings.bodyFont.fontFamily}
            titleColor={settings.titlePrimary}
            titleFontSize={Number(settings.titleFont.fontSize)}
            titleFontFamily={settings.titleFont.fontFamily}
            joinButtonColor={settings.buttonBg}
            joinButtonTextColor={settings.buttonTextColor}
            joinButtonFontSize={settings.buttonFont.fontSize}
            joinButtonFontFamily={settings.buttonFont.fontFamily}
            onSubmit={(values) => {
              if (nextPageRoute === '/confirmation') {
                onSubmit?.(values, catalogOrderRequest);
              }
              setFormValues(values);
              history.push(`${url}${nextPageRoute}${search}`);
            }}
            isPublished={settings.isPublished}
            footerLinks={[
              { label: 'Terms of Service', href: settings?.termsOfServiceUrl || 'https://www.aspire.io/legal/terms-of-service' },
              { label: 'Privacy Policy', href: settings?.privacyPolicyUrl || 'https://www.aspire.io/legal/privacy-policy' },
            ]}
          />
        )}
        </Route>
        <Route path={`${path}/brief`} exact>
          {briefPage && (
          <BriefPagePreview
            brandLogo={settings.logo}
            programName={programTitle}
            brandName={settings.brandName}
            title={briefPage.brief.title}
            description={briefPage.brief.description}
            continueButtonText={briefPage.brief.primaryButtonLabel}
            briefTemplate={termsSettings}
            contentGuideline={contentGuideline}
            onSubmit={() => {
              if (nextPageRoute === '/confirmation') {
                onSubmit?.(formValues, catalogOrderRequest);
              }
              history.push(`${url}${nextPageRoute}${search}`);
            }}
            isSubmitting={isSubmitting}
            backgroundColor={settings.bgPrimary}
          />
        )}
        </Route>
        <Route path={`${path}/product`} exact>
          {productPage && (
          <ProductPagePreview
            catalogPreviewIframeUrl={productPage.product.iframeUrl}
            isCatalogPreviewIframeUrlLoading={!productPage.product.iframeUrl}
            onSubmit={(catalogOrderRequest) => {
              if (nextPageRoute === '/confirmation') {
                onSubmit?.(formValues, catalogOrderRequest);
              }
              setCatalogOrderRequest(catalogOrderRequest);
              history.push(`${url}${nextPageRoute}${search}`);
            }}
            backgroundColor={settings.bgPrimary}
          />
        )}
        </Route>
        <Route path={`${path}/form`} exact>
          {formPage && (
            <Form
              view={viewMode}
              logo={settings.logo}
              programName={programTitle}
              brandName={settings.brandName}
              title={formPage.form.title}
              description={formPage.form.description}
              fields={formPage.form.fields}
              supportEmail={formPage.form.supportEmail}
              primaryButtonLabel={formPage.form.primaryButtonLabel}
              onSubmit={(values) => {
                const allValues = {
                  ...formValues,
                  ...values,
                };
                if (nextPageRoute === '/confirmation') {
                  onSubmit?.(allValues, catalogOrderRequest);
                }
                setFormValues(allValues);
                history.push(`${url}${nextPageRoute}${search}`);
              }}
              backgroundColor={settings.bgPrimary}
            />
          )}
        </Route>
        <Route path={`${path}/confirmation`} exact>
          {confirmationPage && (
            <Confirmation
              view={viewMode}
              backgroundColor={settings.bgPrimary}
              logo={settings.logo}
              programName={programTitle}
              brandName={settings.brandName}
              title={confirmationPage.confirmation.primaryMessage || 'Confirmation Title'}
              description={confirmationPage.confirmation.description || 'Confirmation Description'}
            />
          )}
        </Route>
      </Switch>
    </div>
  );
};
